import FaqStateProvider from './components/FaqStateProvider'
import { useMemo, useState } from 'react'
import CreateGroupModal from './components/Modals/CreateGroupModal'
import useFaqState from './hooks/useFaqState'
import { IFaqArticle, IFaqCategory } from './faq.types'
import { useNavigate } from 'react-router'
import { ReactComponent as ArticleIcon } from '../../../assets/icons/icon-news.svg'
import CategoryTreeRow from './components/CategoryTreeRow'
import ArticlePreviewSheet from './components/ArticlePreviewSheet'
import ActiveStatusTile from './components/ActiveStatusTile'
import FaqArticleSearch from './components/FaqArticleSearch'
import Card from '../../../shared/Cards/Card'
import domUtils from '../../../utils/dom.utils'
import useTreeLeafSelectCtrl from '../../../shared/Tree/ctrl/useTreeLeafSelectCtrl'
import useExpandAll from '../../../shared/Tree/ctrl/useExpandAll'
import useTreeExpandCtrl from '../../../shared/Tree/ctrl/useTreeExpandCtrl'
import { ITreeGroup, ITreeLeaf } from '../../../shared/Tree/tree.types'
import Tree from '../../../shared/Tree'
import TopNavigationLayoutPageContent from 'shared/Layouts/TopNavigationLayout/TopNavigationLayoutPageContent'
import AdminNavigationLayout from 'shared/Layouts/TopNavigationLayout/AdminNavifationLayout'
import ActionsButtonWithMenu from '../../../shared/Buttons/ActionsButtonWithMenu'

const FAQ = () => {
  const navigate = useNavigate()
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const { categories, articles } = useFaqState()

  const treeGroups = useMemo(() => {
    return categories.map((c) => ({ id: c.id, parentId: c.parentId, data: c }))
  }, [categories])

  const treeLeafs = useMemo(() => {
    return articles
      .map((article) => ({ key: article.id, data: article, groupId: article.categoryId }))
      .sort((a, b) => a.data.ordering - b.data.ordering)
  }, [articles])

  const expandAllCtrl = useExpandAll()
  const treeExpandCtrl = useTreeExpandCtrl(
    'etb/support/faq',
    expandAllCtrl.isOpenAll,
    treeGroups,
    treeLeafs,
    expandAllCtrl.setIsOpenAll,
    expandAllCtrl.clickedOnExpandAll,
    expandAllCtrl.setClickedOnExpandAll
  )
  const treeSelectCtrl = useTreeLeafSelectCtrl('etb/support/faq', articles[0]?.id)

  const renderArticle = (leaf: ITreeLeaf<IFaqArticle, IFaqCategory['id']>) => {
    return (
      <div
        className='tree-leaf'
        style={{ width: '100%' }}
        data-selected={leaf.data.id === treeSelectCtrl.selectedItem}
        onClick={(ev) => {
          if (ev.ctrlKey || ev.metaKey) {
            domUtils.openLink(`/admin/knowledge-base/articles/edit/${leaf.data.id}`)
          }
        }}
      >
        <div className='inline-flex-gap' style={{ gap: '0.5em' }}>
          <ArticleIcon style={{ width: '30px', flexShrink: 0 }} />
          <p>{leaf.data.title}</p>
          <ActiveStatusTile active={leaf.data.active} />
        </div>
      </div>
    )
  }

  const renderCategory = (group: ITreeGroup<IFaqCategory, IFaqCategory['id']>) => {
    return <CategoryTreeRow treeGroup={group} />
  }

  const renderSelectedArticlePreview = () => {
    const selectedItem = articles.find((a) => a.id === treeSelectCtrl.selectedItem)
    if (!selectedItem) return null

    return (
      <ArticlePreviewSheet
        article={selectedItem}
        style={{ width: 'calc(40% - 1em)', height: '100%', overflow: 'auto' }}
      />
    )
  }

  return (
    <AdminNavigationLayout>
      <TopNavigationLayoutPageContent type='default'>
        {createGroupModalOpen && <CreateGroupModal onClose={() => setCreateGroupModalOpen(false)} />}
        <div className='flex-space-between'>
          <div className='inline-flex-gap'>
            <h2 className='page--header'>База знаний</h2>
          </div>
          <div>
            <ActionsButtonWithMenu>
              <span className='options-contextmenu-option' onClick={() => setCreateGroupModalOpen(true)}>
                Создать группу
              </span>
              <span
                className='options-contextmenu-option'
                onClick={() => navigate('/admin/knowledge-base/articles/create')}
              >
                Создать статью
              </span>
            </ActionsButtonWithMenu>
          </div>
        </div>
        <FaqArticleSearch expandCtrl={treeExpandCtrl} leafSelectCtrl={treeSelectCtrl} />
        <div className='inline-flex-gap' style={{ height: '100%' }}>
          <Card style={{ width: '60%', height: '100%', overflow: 'auto' }} type='normal'>
            <Tree
              leafs={treeLeafs}
              groups={treeGroups}
              renderLeaf={renderArticle}
              renderGroup={renderCategory}
              expandCtrl={treeExpandCtrl}
              leafSelectCtrl={treeSelectCtrl}
            />
          </Card>
          {renderSelectedArticlePreview()}
        </div>
      </TopNavigationLayoutPageContent>
    </AdminNavigationLayout>
  )
}

export default () => (
  <FaqStateProvider>
    <FAQ />
  </FaqStateProvider>
)
